import { CROWD_SFX_SAMPLE_IDS, Sfx, SfxId, type CrowdSfxSampleId } from '@src/audio/Sfx';
import { Bus, type SoundBusManager } from '@src/audio/SoundBusManager';

const SFX_PATH = '/assets/sfx';

export type CrowdCue = 'cheer' | 'cheerBig' | 'gasp' | 'boo' | 'applause' | 'ooh';

const SAMPLE: Record<CrowdCue, CrowdSfxSampleId> = {
  cheer: 'crowd_cheer',
  cheerBig: 'crowd_cheer_big',
  gasp: 'crowd_gasp',
  boo: 'crowd_boo',
  applause: 'crowd_applause',
  ooh: 'crowd_ooh',
};

const COOLDOWN: Record<CrowdCue, number> = {
  cheer: 2.6,
  cheerBig: 5.5,
  gasp: 2.8,
  boo: 6.5,
  applause: 4.5,
  ooh: 3.2,
};

const VOL: Record<CrowdCue, number> = {
  cheer: 0.34,
  cheerBig: 0.46,
  gasp: 0.38,
  boo: 0.36,
  applause: 0.32,
  ooh: 0.34,
};

const ATTACK: Record<CrowdCue, number> = {
  cheer: 0.05,
  cheerBig: 0.07,
  gasp: 0.03,
  boo: 0.06,
  applause: 0.08,
  ooh: 0.04,
};

const TAIL: Record<CrowdCue, number> = {
  cheer: 0.45,
  cheerBig: 0.7,
  gasp: 0.28,
  boo: 0.55,
  applause: 0.65,
  ooh: 0.32,
};

const GLOBAL_GAP = 0.38;
const MAX_STINGS = 2;
const CHEER_AFTER_BIG = 4.0;

/**
 * Stadium crowd layer on the SFX bus (so Vaelen's sidechain already ducks it).
 * Own voice graph — `Sfx.stopVoices()` must not cut a room-clear cheer.
 */
export class CrowdBed {
  private readonly out: GainNode;
  private stings: GainNode;
  private readonly murmur: GainNode;
  private readonly samples = new Map<string, AudioBuffer | null>();
  private readonly sampleLoading = new Map<string, Promise<AudioBuffer | null>>();
  private readonly lastCue = new Map<CrowdCue, number>();
  private lastAny = -99;
  private lastCheerBig = -99;
  private activeStings = 0;
  private murmurSrc: AudioBufferSourceNode | null = null;
  private bedWanted = 0;
  private parked = false;
  private breathePhase = 0;

  constructor(
    private readonly buses: SoundBusManager,
    private readonly sfx: Sfx,
  ) {
    const ctx = buses.context;
    this.out = ctx.createGain();
    this.out.gain.value = 1;
    this.out.connect(buses.bus(Bus.Sfx));
    this.stings = ctx.createGain();
    this.stings.gain.value = 1;
    this.stings.connect(this.out);
    this.murmur = ctx.createGain();
    this.murmur.gain.value = 0.0001;
    this.murmur.connect(this.out);
  }

  preload(): void {
    for (const id of CROWD_SFX_SAMPLE_IDS) void this.loadSample(id);
  }

  /**
   * Play a crowd sting. `force` is for broadcast set pieces that must land
   * even if a gameplay cheer just fired.
   */
  sting(cue: CrowdCue, intensity = 1, delaySec = 0, force = false): boolean {
    const now = this.buses.now;
    if (!force) {
      if (now - this.lastAny < GLOBAL_GAP) return false;
      const last = this.lastCue.get(cue);
      if (last !== undefined && now - last < COOLDOWN[cue]) return false;
      if (cue === 'cheer' && now - this.lastCheerBig < CHEER_AFTER_BIG) return false;
      if (this.activeStings >= MAX_STINGS) return false;
    }
    this.lastAny = now;
    this.lastCue.set(cue, now);
    if (cue === 'cheerBig') this.lastCheerBig = now;

    const id = SAMPLE[cue];
    const buffer = this.samples.get(id);
    const vol = Math.min(1.15, Math.max(0.08, VOL[cue] * intensity));
    if (buffer) {
      this.playBuffer(buffer, vol, delaySec, ATTACK[cue], TAIL[cue], cue === 'gasp' || cue === 'ooh' ? 0.025 : 0.035);
      return true;
    }
    void this.loadSample(id);
    if (cue === 'applause') {
      this.sfx.play(SfxId.CrowdApplause, intensity, delaySec);
      return true;
    }
    if (cue === 'cheer' || cue === 'cheerBig') {
      this.sfx.play(SfxId.CrowdRoar, cue === 'cheerBig' ? Math.max(1.2, intensity) : intensity, delaySec);
      return true;
    }
    return false;
  }

  /** Target murmur level. 0 stops the bed. Ignored while parked. */
  setMurmur(level: number, fadeSec = 0.7): void {
    this.bedWanted = Math.max(0, Math.min(0.28, level));
    if (this.parked) return;
    this.applyMurmur(this.bedWanted, fadeSec);
  }

  /** Fade murmur and in-flight stings; remember the bed so `unpark()` can restore it. */
  park(fadeSec = 0.22): void {
    this.parked = true;
    this.applyMurmur(0, fadeSec);
    this.fadeStings(fadeSec);
  }

  unpark(fadeSec = 0.55): void {
    this.parked = false;
    this.applyMurmur(this.bedWanted, fadeSec);
  }

  /** Hard stop: clear the wanted bed and fade everything. */
  stopAll(fadeSec = 0.15): void {
    this.bedWanted = 0;
    this.parked = false;
    this.applyMurmur(0, fadeSec);
    this.fadeStings(fadeSec);
  }

  /** Slow swell so the bed is not a flat loop. Call from AudioDirector.update. */
  update(dt: number): void {
    if (this.parked || this.bedWanted <= 0.0002 || !this.murmurSrc) return;
    this.breathePhase += dt * 0.55;
    const pulse = 0.9 + 0.1 * Math.sin(this.breathePhase);
    const ctx = this.buses.context;
    this.murmur.gain.setTargetAtTime(this.bedWanted * pulse, ctx.currentTime, 0.45);
  }

  private fadeStings(fadeSec: number): void {
    const ctx = this.buses.context;
    const t = ctx.currentTime;
    const kill = Math.max(0.05, fadeSec);
    const old = this.stings;
    this.stings = ctx.createGain();
    this.stings.gain.value = 1;
    this.stings.connect(this.out);
    this.activeStings = 0;
    old.gain.cancelScheduledValues(t);
    old.gain.setValueAtTime(Math.max(0.0001, old.gain.value), t);
    old.gain.exponentialRampToValueAtTime(0.0001, t + kill);
    window.setTimeout(() => {
      try {
        old.disconnect();
      } catch {
        // already gone
      }
    }, Math.ceil((kill + 0.2) * 1000));
  }

  private applyMurmur(level: number, fadeSec: number): void {
    const ctx = this.buses.context;
    const t = ctx.currentTime;
    const fade = Math.max(0.08, fadeSec);
    const target = Math.max(0.0001, level);
    if (level > 0.0002) this.ensureMurmur();
    this.murmur.gain.cancelScheduledValues(t);
    this.murmur.gain.setValueAtTime(Math.max(0.0001, this.murmur.gain.value), t);
    this.murmur.gain.exponentialRampToValueAtTime(target, t + fade);
    if (level <= 0.0002) {
      window.setTimeout(() => {
        if (this.bedWanted > 0.0002 && !this.parked) return;
        this.stopMurmurSrc();
      }, Math.ceil((fade + 0.15) * 1000));
    }
  }

  private ensureMurmur(): void {
    if (this.murmurSrc) return;
    const buffer = this.samples.get('crowd_murmur');
    if (!buffer) {
      void this.loadSample('crowd_murmur').then((buf) => {
        if (buf && this.bedWanted > 0.0002 && !this.parked && !this.murmurSrc) this.ensureMurmur();
      });
      return;
    }
    const ctx = this.buses.context;
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    src.loop = true;
    src.connect(this.murmur);
    src.start();
    this.murmurSrc = src;
  }

  private stopMurmurSrc(): void {
    const src = this.murmurSrc;
    if (!src) return;
    this.murmurSrc = null;
    try {
      src.stop();
    } catch {
      // already stopped
    }
    try {
      src.disconnect();
    } catch {
      // already gone
    }
  }

  private playBuffer(
    buffer: AudioBuffer,
    vol: number,
    delaySec: number,
    attack: number,
    fade: number,
    pitchVar: number,
  ): void {
    const ctx = this.buses.context;
    const t = this.buses.now + delaySec;
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    src.playbackRate.value = 1 + (Math.random() * 2 - 1) * pitchVar;
    const g = ctx.createGain();
    const dur = buffer.duration;
    const attackT = Math.min(attack, dur * 0.15);
    const fadeT = Math.min(fade, dur * 0.5);
    const holdEnd = Math.max(attackT + 0.04, dur - fadeT);
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(vol, t + attackT);
    g.gain.setValueAtTime(vol, t + holdEnd);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    src.connect(g);
    g.connect(this.stings);
    src.start(t);
    src.stop(t + dur + 0.05);
    this.activeStings++;
    const doneIn = Math.ceil((delaySec + dur + 0.08) * 1000);
    window.setTimeout(() => {
      this.activeStings = Math.max(0, this.activeStings - 1);
    }, doneIn);
  }

  private loadSample(id: string): Promise<AudioBuffer | null> {
    const cached = this.samples.get(id);
    if (cached !== undefined) return Promise.resolve(cached);
    const inflight = this.sampleLoading.get(id);
    if (inflight) return inflight;
    const p = (async (): Promise<AudioBuffer | null> => {
      try {
        const res = await fetch(`${SFX_PATH}/${id}.mp3`);
        if (!res.ok) return null;
        const type = res.headers.get('content-type') ?? '';
        if (type.includes('text/html')) return null;
        const data = await res.arrayBuffer();
        if (data.byteLength < 4096) return null;
        return await this.buses.context.decodeAudioData(data);
      } catch {
        return null;
      }
    })();
    this.sampleLoading.set(id, p);
    p.then((buffer) => {
      this.samples.set(id, buffer);
      this.sampleLoading.delete(id);
    });
    return p;
  }
}
